import React, { useEffect } from "react";
import { Box, Typography, Card, CardContent, Grid } from "@mui/material";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import { useParams, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import deviceStore from "../../../stores/DeviceStore";
import logStore from "../../../stores/LogStore";
import customerStore from "../../../stores/CustomerStore";
import organizationStore from "../../../stores/OrganizationStore";

const DeviceInfoComponent = observer(() => {
  const { pimios_id } = useParams();
  const deviceId = pimios_id?.split("-")[0];
  const navigate = useNavigate();

  useEffect(() => {
    if (!deviceStore.devices.length) {
      deviceStore.loadDevices();
    }
    if (!customerStore.customers.length) {
      customerStore.fetchCustomers();
    }
    if (!organizationStore.organizations.length) {
      organizationStore.fetchOrganizations();
    }
  }, []);

  const device = deviceStore.devices.find((d) => d.pimios_id === deviceId);

  // const customer =
  //   customerStore.customers?.find((c) => c.id === device.customer_id) || {};
  // const organization =
  //   organizationStore.organizations?.find(
  //     (o) => o.id === customer.organization_id
  //   ) || {};

  function getLatestDeviceVoltage(deviceId) {
    const logIds = logStore.deviceLogIds.get(deviceId);
    if (!logIds || logIds.size === 0) return "Device not connected";
    const logs = Array.from(logIds)
      .map((id) => logStore.logsMap.get(id))
      .filter(Boolean)
      .sort((a, b) => new Date(b.user_local_t) - new Date(a.user_local_t));
    return logs[0]?.system_voltage ?? "Device not connected";
  }

  if (deviceStore.isLoading) {
    return <Typography>Loading device data...</Typography>;
  }

  if (!device) {
    return <Typography>Device not found.</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Banner Card with title and Device ID */}
      <Card
        elevation={3}
        sx={{
          mb: 4,
          p: 2,
          background: "#F3F4F6",
          borderLeft: "4px solid #22C55E",
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            <DevicesOtherIcon
              fontSize="large"
              sx={{ mr: 3, color: "#22C55E" }}
            />
            <Typography variant="h4" fontWeight="bold">
              Device Info
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Device ID: <strong>{device.pimios_id || "Unknown"}</strong>
          </Typography>
        </CardContent>
      </Card>

      {/* Device Details Cards in their own box */}
      <Box sx={{ mb: 4 }}>
        <Grid
          container
          spacing={2}
          sx={{
            width: "100%",
            display: "flex",
          }}
        >
          <Grid item xs={12} md sx={{ flex: 1, minWidth: 0, display: "flex" }}>
            <Card
              elevation={2}
              sx={{
                p: 2,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Type
              </Typography>
              <Typography variant="h6">{device.type_name}</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md sx={{ flex: 1, minWidth: 0, display: "flex" }}>
            <Card
              elevation={2}
              sx={{
                p: 2,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Manufacturer
              </Typography>
              <Typography variant="h6">
                {device.manufacture || "Unknown"}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md sx={{ flex: 1, minWidth: 0, display: "flex" }}>
            <Card
              elevation={2}
              sx={{
                p: 2,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Box display="flex" alignItems="center">
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    mr: 1,
                    bgcolor: logStore.connectedDeviceIds.has(device.pimios_id)
                      ? "#22C55E"
                      : "#EF4444",
                  }}
                />
                <Typography variant="h6" component="span">
                  {logStore.connectedDeviceIds.has(device.pimios_id)
                    ? "Connected"
                    : "Not Connected"}
                </Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md sx={{ flex: 1, minWidth: 0, display: "flex" }}>
            <Card
              elevation={2}
              sx={{
                p: 2,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Customer
              </Typography>
              {/* <Typography variant="h6">
                {customer.name && organization.name
                  ? `${customer.name}, (${organization.name})`
                  : "Unknown"}
              </Typography> */}
            </Card>
          </Grid>
          <Grid item xs={12} md sx={{ flex: 1, minWidth: 0, display: "flex" }}>
            <Card
              elevation={2}
              sx={{
                p: 2,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Current Voltage
              </Typography>
              <Typography variant="h6">
                {logStore.connectedDeviceIds.has(device.pimios_id)
                  ? `${getLatestDeviceVoltage(device.pimios_id)} V`
                  : "Device not connected"}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Alert Config Button in its own box */}
      <Box width="100%" display="flex" justifyContent="flex-end">
        <button
          style={{
            backgroundColor: "#22C55E",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "10px 24px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
          onClick={() =>
            navigate(`/alerts/config/${device.pimios_id}`, {
              state: {
                deviceRecord: {
                  pimios_id: device.pimios_id,
                  type_name: device.type_name,
                  name: device.name,
                  alert_config_id: device.alert_config_id,
                },
              },
            })
          }
        >
          View Alert Config
        </button>
      </Box>
    </Box>
  );
});

export default DeviceInfoComponent;
